/**
 * First-person controller for Hurka — Issue #3.
 *
 * Two look modes, no hover-look this time (Issue #15):
 *
 *   1. Pointer-Lock (preferred).  Click on the canvas → mouse disappears,
 *      relative mouse motion (movementX/Y) drives camera rotation.
 *      ESC releases.
 *
 *   2. Drag-to-Look (fallback when pointer-lock isn't granted — some
 *      browsers refuse it under certain conditions).  Hold the left
 *      mouse button + move the mouse to rotate the camera.
 *
 * A static DOM crosshair stays at the screen centre the whole time
 * (see index.html / style.css), which is what the user aims with.  When
 * pointer-lock is active the OS cursor is hidden anyway; when it isn't,
 * the OS cursor + the central crosshair both sit on screen.
 *
 * WASD is always active, regardless of look mode.
 */

import { UniversalCamera } from '@babylonjs/core/Cameras/universalCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Scene } from '@babylonjs/core/scene';

export interface FPSController {
  dispose(): void;
  isLocked(): boolean;
}

export function setupPointerLockControls(
  canvas: HTMLCanvasElement,
  camera: UniversalCamera,
  scene: Scene,
  moveSpeed = 6.0,
  lookSensitivity = 0.003,
  jumpHeight = 0.6,
  jumpCooldownMs = 400
): FPSController {
  let locked = false;
  let dragging = false;
  let dragLastX = 0;
  let dragLastY = 0;
  let lastJump = 0;
  let onGround = true;

  const move = {
    forward: false,
    backward: false,
    left: false,
    right: false,
  };

  function setStatus(text: string): void {
    const el = document.getElementById('hud-status');
    if (el) el.textContent = text;
  }
  setStatus('WASD = move · click = lock mouse · left-click+drag = look');

  // ---- Pointer Lock ----
  function onLockChange(): void {
    locked = document.pointerLockElement === canvas;
    if (locked) {
      setStatus('mouse captured — ESC to release · left-click = attack');
      dragging = false;
    } else {
      setStatus('WASD = move · click = lock mouse · left-click+drag = look');
    }
  }
  document.addEventListener('pointerlockchange', onLockChange);

  // ---- Mousedown: try pointer-lock + start drag fallback ----
  canvas.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return;

    // Request pointer-lock.  If granted, the mousemove handler will
    // receive relative `movementX/Y` and the cursor disappears.
    const p = canvas.requestPointerLock();
    if (p && typeof (p as Promise<void>).catch === 'function') {
      (p as Promise<void>).catch(() => {
        // Browser denied — drag-to-look still works below.
      });
    }

    // Drag-to-look fallback for browsers / setups that refuse lock.
    dragging = true;
    dragLastX = e.clientX;
    dragLastY = e.clientY;
  });

  // ---- Mouseup: stop dragging ----
  window.addEventListener('mouseup', () => {
    dragging = false;
  });

  // ---- Mousemove: lock uses relative, drag uses absolute ----
  function onMouseMove(e: MouseEvent): void {
    let dx = 0;
    let dy = 0;

    if (locked) {
      dx = e.movementX || 0;
      dy = e.movementY || 0;
    } else if (dragging) {
      dx = e.clientX - dragLastX;
      dy = e.clientY - dragLastY;
      dragLastX = e.clientX;
      dragLastY = e.clientY;
    } else {
      return;
    }

    applyLook(dx, dy);
  }
  document.addEventListener('mousemove', onMouseMove);

  function applyLook(dx: number, dy: number): void {
    camera.rotation.y += dx * lookSensitivity;
    camera.rotation.x += dy * lookSensitivity;

    const limit = Math.PI / 2 - 0.01;
    if (camera.rotation.x > limit) camera.rotation.x = limit;
    if (camera.rotation.x < -limit) camera.rotation.x = -limit;
  }

  // ---- Keyboard ----
  function onKey(e: KeyboardEvent, down: boolean): void {
    switch (e.code) {
      case 'KeyW':
      case 'ArrowUp':
        move.forward = down;
        break;
      case 'KeyS':
      case 'ArrowDown':
        move.backward = down;
        break;
      case 'KeyA':
      case 'ArrowLeft':
        move.left = down;
        break;
      case 'KeyD':
      case 'ArrowRight':
        move.right = down;
        break;
      case 'Space':
        if (down) tryJump();
        e.preventDefault();
        break;
      case 'Escape':
        // Browser releases pointer-lock automatically on ESC.
        break;
    }
  }
  window.addEventListener('keydown', (e) => onKey(e, true));
  window.addEventListener('keyup', (e) => onKey(e, false));

  function tryJump(): void {
    const now = performance.now();
    if (now - lastJump < jumpCooldownMs) return;
    if (!onGround) return;
    camera.position.y += jumpHeight;
    lastJump = now;
  }

  // ---- Movement + gravity per frame ----
  const tmpForward = new Vector3();
  const tmpRight = new Vector3();
  const tmpMove = new Vector3();

  scene.onBeforeRenderObservable.add(() => {
    const dt = scene.getEngine().getDeltaTime() / 1000;

    tmpForward.set(
      Math.sin(camera.rotation.y),
      0,
      Math.cos(camera.rotation.y)
    );
    tmpRight.set(
      Math.cos(camera.rotation.y),
      0,
      -Math.sin(camera.rotation.y)
    );

    tmpMove.set(0, 0, 0);
    if (move.forward) tmpMove.addInPlace(tmpForward);
    if (move.backward) tmpMove.subtractInPlace(tmpForward);
    if (move.right) tmpMove.addInPlace(tmpRight);
    if (move.left) tmpMove.subtractInPlace(tmpRight);

    if (tmpMove.lengthSquared() > 1e-8) {
      tmpMove.normalize();
      tmpMove.scaleInPlace(moveSpeed * dt);
    }

    if (tmpMove.lengthSquared() > 0) {
      camera.position.x += tmpMove.x;
      camera.position.z += tmpMove.z;
    }
  });

  // Ground check + gravity nudge.
  const BASE_Y = 1.7;
  scene.onAfterRenderObservable.add(() => {
    if (camera.position.y < BASE_Y - 0.5) {
      camera.position.y -= 9.81 * (scene.getEngine().getDeltaTime() / 1000);
      onGround = false;
    } else if (Math.abs(camera.position.y - BASE_Y) < 0.05) {
      onGround = true;
    }
  });

  // Babylon's collision / gravity system needs the input manager alive
  // to update the ellipsoid each frame.  We don't use its inputs (we
  // drive WASD and look ourselves), so pass false to skip
  // preventDefault on canvas events.
  camera.attachControl(canvas, false);

  return {
    dispose() {
      document.removeEventListener('pointerlockchange', onLockChange);
      document.removeEventListener('mousemove', onMouseMove);
      camera.detachControl();
    },
    isLocked: () => locked,
  };
}
